/**
 * @name: Notify Popup
 * @date: 2026-08-14
 * @description: Global notify popup opened by matching header menu labels
 * @dependencies: snippets/notify-popup.liquid, assets/notify-popup.css
 * @strict-mode: true
 */

(function () {
  'use strict';

  const SCROLL_LOCK_CLASS = 'notify-popup-open';
  const PINNED_CLASS = 'notify-popup-pinned';
  let scrollLockCount = 0;
  let scrollPosition = 0;
  let lastFocusedElement = null;
  let pinnedMenuItem = null;

  function normalizeLabel(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function parseMenuItems(raw) {
    if (!raw) return [];

    return raw
      .split('||')
      .map(function (item) {
        return normalizeLabel(item);
      })
      .filter(Boolean);
  }

  function lockScroll() {
    if (scrollLockCount === 0) {
      scrollPosition = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
      document.body.classList.add(SCROLL_LOCK_CLASS);
    }

    scrollLockCount += 1;
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount > 0) return;

    document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    document.body.classList.remove(SCROLL_LOCK_CLASS);
  }

  function getLinkLabel(element) {
    const title = element.querySelector('.mega-menu__title, .mobile-mega-menu__title');
    if (title) return title.textContent;

    return element.textContent;
  }

  function isHeaderMenuLink(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('[data-collection-drop-section]')) return false;

    return Boolean(
      element.closest(
        '.header-desktop, .site-header, [data-mobile-menu], [data-mobile-nav], .mobile-mega-menu, .site-nav'
      )
    );
  }

  function pinMenuFromLink(link) {
    const menuItem = link.closest('.has-megamenu');
    if (!menuItem) return;

    pinnedMenuItem = menuItem;
    pinnedMenuItem.classList.add(PINNED_CLASS);
    pinnedMenuItem.classList.remove('is-force-closed');
  }

  function isPointInside(element, point) {
    if (!point) return false;

    const rect = element.getBoundingClientRect();

    return (
      point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
    );
  }

  function closePinnedMegaMenu(point) {
    if (!pinnedMenuItem) return;

    const menuItem = pinnedMenuItem;
    const megaMenu = menuItem.querySelector('.mega-menu, [data-mega-menu]');

    pinnedMenuItem = null;
    menuItem.classList.remove(PINNED_CLASS, 'is-active');

    if (megaMenu) {
      megaMenu.classList.remove('is-active');
    }

    // `is-force-closed` blocks the next hover, so only use it when the pointer
    // is still over the nav item (which would otherwise reopen immediately),
    // and always clear it once the pointer leaves.
    if (!isPointInside(menuItem, point)) return;

    menuItem.classList.add('is-force-closed');
    menuItem.addEventListener(
      'mouseleave',
      function () {
        menuItem.classList.remove('is-force-closed');
      },
      { once: true }
    );
  }

  function initNotifyPopup() {
    const modal = document.querySelector('[data-notify-popup]');
    if (!modal) return;

    const menuItemSet = new Set(parseMenuItems(modal.getAttribute('data-notify-menu-items')));
    if (!menuItemSet.size) return;

    const dialog = modal.querySelector('.notify-popup__dialog');

    function openModal(fromLink) {
      lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      pinMenuFromLink(fromLink);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      lockScroll();
    }

    function closeModal(event) {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      unlockScroll();

      const point =
        event && typeof event.clientX === 'number' && (event.clientX || event.clientY)
          ? { x: event.clientX, y: event.clientY }
          : null;

      closePinnedMegaMenu(point);

      if (lastFocusedElement) {
        lastFocusedElement.blur();
        lastFocusedElement = null;
      }
    }

    modal._closeNotifyPopup = closeModal;

    modal.querySelectorAll('[data-notify-popup-close]').forEach(function (closeTrigger) {
      closeTrigger.addEventListener('click', closeModal);
    });

    // The header closes its mega-menu on any document click outside the nav, so
    // keep popup interactions from reaching it. Inner form handlers run first.
    modal.addEventListener('click', function (event) {
      if (dialog && dialog.contains(event.target)) {
        event.stopPropagation();
      }
    });

    // Capture phase so header link handlers cannot navigate away instead.
    document.addEventListener(
      'click',
      function (event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (modal.contains(target)) return;

        const link = target.closest('a');
        if (!link || !isHeaderMenuLink(link)) return;
        if (!menuItemSet.has(normalizeLabel(getLinkLabel(link)))) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        openModal(link);
      },
      true
    );

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (modal.hidden) return;
      closeModal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNotifyPopup);
  } else {
    initNotifyPopup();
  }
})();
